import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiX, FiDownload } from 'react-icons/fi';
import './WebinarStatsModal.css';

const ACCESS_STATUS_LABELS = {
  active: 'Активен',
  unlimited: 'Бессрочно',
  expired: 'Истёк',
  revoked: 'Отозван',
};

const WebinarStatsModal = ({ open, onClose, webinarId }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && webinarId) {
      fetchStats();
    }
  }, [open, webinarId]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/webinars/${webinarId}/registrations/stats/`);
      setStats(response.data);
    } catch (error) {
      toast.error('Ошибка загрузки статистики');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`/api/webinars/${webinarId}/registrations/export/`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `registrations_webinar_${webinarId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Данные выгружены');
    } catch (error) {
      toast.error('Ошибка выгрузки данных');
    }
  };

  const handleRevoke = async (registrationId) => {
    try {
      await axios.post(`/api/webinars/${webinarId}/registrations/${registrationId}/revoke/`);
      toast.success('Доступ отозван');
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Не удалось отозвать доступ');
    }
  };

  const handleRestore = async (registrationId) => {
    try {
      await axios.post(`/api/webinars/${webinarId}/registrations/${registrationId}/restore/`);
      toast.success('Доступ возвращён');
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Не удалось вернуть доступ');
    }
  };

  if (!open) return null;

  return (
    <div className="stats-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="stats-modal-content">
        <div className="stats-modal-header">
          <h2>Статистика просмотров</h2>
          <button className="stats-modal-close" onClick={onClose}>
            <FiX />
          </button>
        </div>

        <div className="stats-modal-body">
          {loading ? (
            <div className="loading"><div className="spinner"></div></div>
          ) : stats ? (
            <>
              <div className="stats-summary">
                <div className="stat-summary-item">
                  <h3>Всего регистраций</h3>
                  <p className="stat-value">{stats.total || 0}</p>
                </div>
                {stats.by_access_status && (
                  <>
                    <div className="stat-summary-item">
                      <h3>Доступ открыт</h3>
                      <p className="stat-value">
                        {(stats.by_access_status.active || 0) + (stats.by_access_status.unlimited || 0)}
                      </p>
                    </div>
                    <div className="stat-summary-item">
                      <h3>Закрыт</h3>
                      <p className="stat-value">
                        {(stats.by_access_status.expired || 0) + (stats.by_access_status.revoked || 0)}
                      </p>
                    </div>
                  </>
                )}
                {stats.by_link && Object.keys(stats.by_link).length > 0 && (
                  <div className="stat-summary-item">
                    <h3>По ссылкам</h3>
                    <div className="by-link-stats">
                      {Object.entries(stats.by_link).map(([linkName, count]) => (
                        <div key={linkName} className="by-link-item">
                          <span className="link-name">{linkName}</span>
                          <span className="link-count">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="stats-actions">
                <button className="btn btn-primary" onClick={handleExport}>
                  <FiDownload /> Выгрузить данные (CSV)
                </button>
              </div>

              {stats.registrations && stats.registrations.length > 0 ? (
                <div className="registrations-table-container">
                  <h3>Список регистраций</h3>
                  <div className="registrations-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Фамилия</th>
                          <th>Имя</th>
                          <th>Отчество</th>
                          <th>Email</th>
                          <th>Компания</th>
                          <th>Должность</th>
                          <th>Город</th>
                          <th>Голограммы</th>
                          <th>Дата регистрации</th>
                          <th>Доступ до</th>
                          <th>Статус</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.registrations.map((reg) => (
                          <tr key={reg.id}>
                            <td>{reg.last_name}</td>
                            <td>{reg.first_name}</td>
                            <td>{reg.middle_name || '-'}</td>
                            <td>{reg.email || '-'}</td>
                            <td>{reg.company || '-'}</td>
                            <td>{reg.position || '-'}</td>
                            <td>{reg.city || '-'}</td>
                            <td>{reg.holograms && reg.holograms.length > 0 ? reg.holograms.join(', ') : '-'}</td>
                            <td>{new Date(reg.registered_at).toLocaleString('ru-RU')}</td>
                            <td>{reg.access_expires_at ? new Date(reg.access_expires_at).toLocaleString('ru-RU') : 'Без срока'}</td>
                            <td>
                              <span className={`access-status-badge access-status-${reg.access_status || 'unlimited'}`}>
                                {ACCESS_STATUS_LABELS[reg.access_status] || reg.access_status || '—'}
                              </span>
                            </td>
                            <td>
                              {reg.access_status === 'revoked' ? (
                                <button type="button" className="btn btn-outline" onClick={() => handleRestore(reg.id)}>
                                  Вернуть
                                </button>
                              ) : (
                                <button type="button" className="btn btn-outline" onClick={() => handleRevoke(reg.id)}>
                                  Отозвать
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="empty-state">
                  <p>Нет зарегистрированных пользователей</p>
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">
              <p>Не удалось загрузить статистику</p>
            </div>
          )}
        </div>

        <div className="stats-modal-footer">
          <button className="btn btn-outline" onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
};

export default WebinarStatsModal;
